import _ from "lodash";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  deleteReservationsAsync,
  deleteVisitorsAsync,
  getResendInviteMessageAsync,
  getReservationListAsync,
  getVisitorListAsync,
  patchMemberContractManageDeleteAsync,
} from "src/api/contract/contract-api";
import { ContractReservationListParams, MemberContractManageModel } from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import { RvReservationStatusCode } from "src/api/reservation/reservation-type";
import { BaseButton, ConfirmModal } from "src/components";
import BaseButtonSm from "src/components/BaseButtonSm";
import { VisitStatus } from "src/api/visitor/visitor-types";
import { convertKoreanPhoneNumber, NO_END_TIME, ViewYmdFormat, YmdFormat } from "src/utils/common-util";
import { highlightSearchText } from "src/utils/highlight-util";

// 컴포넌트 Props 타입 정의
type Props = {
  contractManage: MemberContractManageModel; // 계약 관리 정보
  fetchContract: (contractId: string) => void; // 계약 정보 조회 함수
  setSearchText: (searchText: string) => void; // 검색어 설정 함수
  searchText: string; // 검색어
};

// 모달 상태 타입 정의
type Modal = {
  isOpen: boolean; // 모달 표시 여부
  message?: string; // 모달 메시지
};

// 이용자 정보 컴포넌트
// 계약 이용자의 정보를 표시하고 관리하는 컴포넌트
const UserItem = ({ contractManage, fetchContract, setSearchText, searchText }: Props) => {
  const { t } = useTranslation(["user", "common"]);

  // URL 파라미터에서 계약 ID 가져오기
  const { contractId } = useParams();
  // 상태 관리
  const [isDelete, setIsDelete] = useState(false); // 삭제 상태
  const [isDeleted, setIsDeleted] = useState(false); // 삭제 완료 상태
  const [isDeleteReservations, setIsDeleteReservations] = useState(false); // 예약 삭제 상태
  const [isDeleteInvitations, setIsDeleteInvitations] = useState(false); // 초대 삭제 상태
  const [isDeleteReservations2, setIsDeleteReservations2] = useState(false); // 예약 삭제 완료 상태
  const [isDeleteInvitations2, setIsDeleteInvitations2] = useState(false); // 초대 삭제 완료 상태
  const [isReinviteModal, setIsReinviteModal] = useState<Modal>({ isOpen: false, message: "" }); // 재발송 모달 상태
  // 잔여 예약 및 초대 정보 상태
  const [remainingSharedSpaceReservation, setRemainingSharedSpaceReservation] = useState<Number>(0); // 잔여 공용공간 예약 수
  const [remainingVisitorInvitations, setRemainingVisitorInvitations] = useState<Number>(0); // 잔여 방문자 초대 수
  const [remainingSharedSpaceReservationList, setRemainingSharedSpaceReservationList] = useState<any>(); // 잔여 공용공간 예약 목록
  const [remainingVisitorInvitationsList, setRemainingVisitorInvitationsList] = useState<any>(); // 잔여 방문자 초대 목록

  // API 파라미터 설정
  // 잔여 방문자 초대는 사용 + 대기를 합산한다. 대기 건도 이용자를 지우면 함께 취소되므로 잔여로 센다
  const pv: any = {
    contractApplyNumber: contractManage.contractApplyNumber!,
    visitStatuses: [VisitStatus.VISIT_USE, VisitStatus.VISIT_PENDING],
    searchStartTime: moment().format(YmdFormat.WITH_TIME_ZONE),
    searchEndTime: NO_END_TIME,
  };
  // contractId 는 조회 직전에 가드를 통과한 값으로 채운다
  const pr: Omit<ContractReservationListParams, "contractId"> = {
    statusCode: [RvReservationStatusCode.ACCEPTED, RvReservationStatusCode.ACKNOWLEDGED, RvReservationStatusCode.INCOMPLETE],
    organizerMemberNo: [],
    greaterThanEqualsEnd: moment().format(YmdFormat.WITH_TIME_ZONE),
  };

  // API 파라미터 상태 관리
  const [paramsVisitor, setParamsVisitor] = useState<any>(pv);
  const [paramsReservation] = useState<Omit<ContractReservationListParams, "contractId">>(pr);

  // API 호출 훅
  const { executeAsync: getVisitorList } = useApiOperation(getVisitorListAsync, { noHandleError: true });
  const { executeAsync: getResendInviteMessage } = useApiOperation(getResendInviteMessageAsync, { noHandleError: true });
  const { executeAsync: getReservationList } = useApiOperation(getReservationListAsync, { noHandleError: true });
  const { executeAsync: delVisiterByInviter } = useApiOperation(deleteVisitorsAsync);
  const { executeAsync: delReservation } = useApiOperation(deleteReservationsAsync);
  const { executeAsync: patchMemberContractManageDelete } = useApiOperation(patchMemberContractManageDeleteAsync);

  // 모달 상태 관리
  const [alertModal, setAlertModal] = useState<Modal>({ isOpen: false }); // 알림 모달
  const [confirmModal, setConfirmModal] = useState<Modal>({ isOpen: false }); // 확인 모달

  // 회원의 공용공간 예약 목록 조회
  const getMemberReservationList = async (params: Omit<ContractReservationListParams, "contractId">) => {
    const { memberNo } = contractManage;
    if (!contractId || !memberNo) {
      return;
    }

    const response = await getReservationList({ ...params, contractId, organizerMemberNo: [memberNo] });
    const content = response?.data?.data?.content;
    if (!content) {
      return;
    }

    // 잔여 건수는 서버가 센 전체 건수로 읽는다. content.length 는 현재 페이지 분량이라
    // 페이지 크기에 잘리면 "잔여 없음"으로 오판해 삭제 버튼이 잘못 열린다
    setRemainingSharedSpaceReservation(Number(response?.data?.meta?.pageMeta?.totalElements || 0));
    setRemainingSharedSpaceReservationList(content);
  };
  // 회원이 초대한 방문자의 목록을 조회
  const getMembersInvitationVisitors = async (params: any) => {
    if (!params.contractId) {
      params.contractId = contractId;
    }
    if (!params.inviteeMemberNo) {
      params.inviteeMemberNo = contractManage.memberNo;
    }

    const response: any = await getVisitorList(params);
    if (response.data.data.contractVisitor) {
      setRemainingVisitorInvitationsList(response.data.data.contractVisitor);
      // 예약과 같은 이유로 전체 건수를 쓴다
      setRemainingVisitorInvitations(Number(response?.data?.meta?.pageMeta?.totalElements || 0));
    }
  };

  // 이용자 삭제 처리
  const deleteContractManage = useCallback(async () => {
    try {
      if (!contractId) throw Error("contractId is not found");

      const { data } = await patchMemberContractManageDelete({
        contractManageId: Number(contractManage.contractManageId),
        contractMemberId: Number(contractManage.contractMemberId),
        isDeleted: true,
      });

      if (data?.data?.contractMemberId) {
        setSearchText("");
        setAlertModal({ isOpen: true, message: t("userList.deleted") });
      }
    } catch (error: any) {
      console.error("deleteContractManage error", error);

      let errorMessage = t("userList.deleteFailed");
      if (error?.response?.data?.meta?.errorCode === "eCT607") {
        errorMessage = t("userList.contractorCannotDelete");
      }

      setAlertModal({ isOpen: true, message: errorMessage });
    }
  }, [patchMemberContractManageDelete, contractId, contractManage, t]);

  // 확인 모달 확인 버튼 클릭 핸들러
  const clickModalConfirm = useCallback(() => {
    setConfirmModal({ isOpen: false });
    deleteContractManage();
  }, [deleteContractManage]);

  // 알림 모달 확인 버튼 클릭 핸들러
  const clickAlertModalConfirm = useCallback(async () => {
    if (!contractId) throw Error("contractId is not found");

    await fetchContract(contractId);
    setAlertModal({ isOpen: false });
  }, [contractId]);

  // 삭제 가능 여부 확인
  const delDisable = useMemo(() => {
    return Number(remainingSharedSpaceReservation) !== 0 || Number(remainingVisitorInvitations) !== 0;
  }, [remainingSharedSpaceReservation, remainingVisitorInvitations]);

  // 잔여 공용공간 삭제 처리
  const deleteReservation = async () => {
    // 모든 예약 삭제를 병렬로 처리
    const deletePromises = remainingSharedSpaceReservationList.map((reservation: any) =>
      delReservation({ contractId: String(contractId), reservationId: String(reservation.id) }),
    );

    const results = await Promise.allSettled(deletePromises);

    // 모든 요청이 성공했는지 확인
    const allSuccessful = results.every((result) => result.status === "fulfilled");

    if (allSuccessful) {
      await getMemberReservationList(paramsReservation);
      setIsDeleteReservations2(true);
      return; // 성공시 더 이상 처리하지 않음
    }

    // 실패한 요청들 재시도
    const failedIndices = results
      .map((result: any, index: number) => (result.status === "rejected" ? index : -1))
      .filter((index: number) => index !== -1);

    if (failedIndices.length > 0) {
      console.log(`${failedIndices.length}개 요청 재시도 중...`);

      const retryPromises = failedIndices.map((index: number) =>
        delReservation({
          contractId: String(contractId),
          reservationId: String(remainingSharedSpaceReservationList[index].id),
        }),
      );

      const retryResults = await Promise.allSettled(retryPromises);
      const retryAllSuccessful = retryResults.every((result: any) => result.status === "fulfilled");

      if (retryAllSuccessful) {
        console.log("재시도 성공");
        await getMemberReservationList(paramsReservation);
        setIsDeleteReservations2(true);
      } else {
        console.error("일부 예약 삭제에 실패했습니다.");
      }
    }
  };

  // 잔여 방문자 초대 삭제 처리
  const deleteVisitors = async (contractManage: MemberContractManageModel) => {
    let chunk: any = [];

    for (let i = 0; i < remainingVisitorInvitationsList.length; i++) {
      chunk.push(remainingVisitorInvitationsList[i].contractVisitorId);
    }

    const rtnData: any = await delVisiterByInviter({ inviteeMemberNo: contractManage.memberNo!, contractVisitorIds: chunk });
    if (rtnData.status > 199 && rtnData.status < 399) {
      await getMembersInvitationVisitors(paramsVisitor);
      setIsDeleteInvitations2(true);
    }
  };

  // 예약 삭제 실행
  const deleteReservations = async () => {
    setIsDeleteReservations(false);

    await deleteReservation();
  };

  // 초대 삭제 실행
  const deleteInvitations = async (contractManage: MemberContractManageModel) => {
    setIsDeleteInvitations(false);
    await deleteVisitors(contractManage);
  };

  const handleReinvite = () => {
    setIsReinviteModal({ isOpen: true, message: "?" });
  };
  const handleConfirmReinviteModal = (contractMemberId: number) => {
    setIsReinviteModal({ isOpen: false });
    reInvite(contractMemberId);
  };
  const reInvite = async (contractMemberId: number) => {
    const { status } = await getResendInviteMessage({ contractMemberId });
    if (status > 199 && status < 399) {
      setAlertModal({ isOpen: true, message: t("userList.reinviteCompleted") });
    } else {
      setAlertModal({ isOpen: true, message: t("userList.resendFailed") });
    }
  };
  // 이용자 삭제
  const onDeleteUser = async (contractManage: any) => {
    // ======== Hotfix 처리 > Harry 수정 > QA-2925 =====
    // 가입된 회원인 경우에만 잔여 공용공간 예약 및 방문자 초대 조회 (memberNo가 있는 경우)
    if (contractManage.memberNo) {
      if (contractId) {
        await getMemberReservationList(paramsReservation);
      }
      if (contractManage.contractManageId) {
        setParamsVisitor({
          ...paramsVisitor,
          contractId: contractId,
          contractManageId: contractManage.contractManageId,
        });
        await getMembersInvitationVisitors({
          ...paramsVisitor,
          contractId: contractId,
          contractManageId: contractManage.contractManageId,
        });
      }
    }
    // 3. 0.5초안에 호출된 가장 마지막 api만 서버로 호출합니다.
  };

  // 검색어 highlight 처리
  const convertHighlightText = (source?: string, _searchValue?: string): React.ReactNode => {
    if (!source || !_searchValue) {
      return source;
    }

    const result = highlightSearchText({ source, searchText: _searchValue });
    return result;
  };

  return (
    <div className="contents-card">
      <div className="contents-card__main-area">
        <div className="left-section">
          <p>
            {contractManage.inviteMobileNumber
              ? convertHighlightText(convertKoreanPhoneNumber(contractManage.inviteMobileNumber), searchText)
              : convertHighlightText(contractManage.inviteEmail, searchText)}
          </p>
          <p>
            {contractManage.email
              ? convertHighlightText(contractManage.email, searchText)
              : contractManage.phoneNumber
              ? convertHighlightText(convertKoreanPhoneNumber(contractManage.phoneNumber), searchText)
              : contractManage.memberStatus === "MEMBER_INVITE"
              ? t("userList.inviteComplete")
              : t("userList.expiredInvite")}
          </p>
        </div>
        <div className="right-section">
          <div>
            <p>{`${moment(contractManage.inviteDate).format(ViewYmdFormat.YYYY_MM_DD_HH_MM)}`}</p>
          </div>
          {contractManage.memberStatus !== "MEMBER_DELETE" && contractManage.memberStatus !== "MEMBER_CLOSE" && (
            <div>
              {(contractManage.isAdmin || contractManage.isContractor) && (
                <div className="base-chip">
                  <span>{t("userList.master")}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {!(contractManage.isAdmin || contractManage.isContractor) && (
        <div className="contents-card__sub-area">
          <div>
            {contractManage.memberStatus === "MEMBER_INVITE" && (
              <BaseButton
                title={t("userList.resend")}
                className="base-btn color-white size-xsmall"
                onClick={() => {
                  handleReinvite();
                }}
              />
            )}
          </div>
          {contractManage.memberStatus !== "MEMBER_DELETE" && contractManage.memberStatus !== "MEMBER_CLOSE" && (
            <>
              <div>
                {contractManage.memberStatus === "MEMBER_EXPIRE" && (
                  <button
                    type="button"
                    data-invite-mobile-number={contractManage.inviteMobileNumber}
                    data-contract-member-id={contractManage.contractMemberId}
                    className="only-text-btn pr10 btn-reinvite"
                    style={{ borderRight: "1px solid #999999" }}
                  >
                    {t("userList.reinvite")}
                  </button>
                )}
                <button
                  type="button"
                  className="only-text-btn pl10 btn-delete"
                  onClick={async () => {
                    setConfirmModal({ isOpen: true });
                    onDeleteUser(contractManage);
                  }}
                >
                  {t("userList.delete")}
                </button>
                <ConfirmModal
                  className="minmax350 px20"
                  isOpen={confirmModal.isOpen}
                  btnLeftTitle={t("common:cancel")}
                  btnRightTitle={t("userList.delete")}
                  btnRightDisable={delDisable}
                  onClose={() => setConfirmModal({ isOpen: false })}
                  onClick={() => clickModalConfirm()}
                  children={
                    <div>
                      <p className="font18 font-weight-600">{t("userList.deleteUser")}</p>
                      {contractManage.memberStatus !== "MEMBER_INVITE" && (
                        <>
                          <div>
                            <p className="font15 font-weight-400 mt20">
                              <Trans t={t} i18nKey="userList.remainingReservationDesc" components={{ br: <br /> }} />
                            </p>
                          </div>

                          <div className="flex-center-between minmax310 mt20" style={{ backgroundColor: "#F2F4F6", height: 52, padding: 20 }}>
                            <div>{t("userList.remainingReservation", { count: Number(remainingSharedSpaceReservation) })}</div>
                            <button
                              className={`only-text-btn pl10 btn-delete size-30 ${
                                Number(remainingSharedSpaceReservation) === 0 ? "text-primary5" : ""
                              }`}
                              disabled={Number(remainingSharedSpaceReservation) === 0}
                              onClick={(e) => {
                                e.preventDefault();
                                setIsDeleteReservations(true);
                              }}
                            >
                              {t("userList.delete")}
                            </button>
                          </div>

                          <div className="flex-center-between minmax310 mt10" style={{ backgroundColor: "#F2F4F6", height: 52, padding: 20 }}>
                            <div>{t("userList.remainingVisitor", { count: Number(remainingVisitorInvitations) })}</div>
                            <button
                              className={`only-text-btn pl10 btn-delete size-30 ${Number(remainingVisitorInvitations) === 0 ? "text-primary5" : ""}`}
                              disabled={Number(remainingVisitorInvitations) === 0}
                              onClick={(e) => {
                                e.preventDefault();
                                setIsDeleteInvitations(true);
                              }}
                            >
                              {t("userList.delete")}
                            </button>
                          </div>
                          <p className="font15 font-weight-400 mt20">{t("userList.deleteImmediateDesc1")}</p>
                          <p className="font15 font-weight-400 mt5">{t("userList.deleteImmediateDesc2")}</p>
                        </>
                      )}
                    </div>
                  }
                />

                {/* 공용공간 삭제 */}
                <ConfirmModal
                  isOpen={isDeleteReservations}
                  btnLeftTitle={t("common:cancel")}
                  btnRightTitle={t("userList.delete")}
                  onClose={() => setIsDeleteReservations(false)}
                  onClick={() => deleteReservations()}
                >
                  <div>
                    <p className="font18 font-weight-600">{t("userList.deleteReservationConfirm")}</p>
                  </div>
                </ConfirmModal>

                {/* 방문자 초대 삭제 */}
                <ConfirmModal
                  isOpen={isDeleteInvitations}
                  btnLeftTitle={t("common:cancel")}
                  btnRightTitle={t("userList.delete")}
                  onClose={() => setIsDeleteInvitations(false)}
                  onClick={() => deleteInvitations(contractManage)}
                >
                  <div>
                    <p className="font18 font-weight-600">{t("userList.deleteVisitorConfirm")}</p>
                  </div>
                </ConfirmModal>
                <ConfirmModal isOpen={isDeleteReservations2} btnRightTitle={t("common:confirm")} onClick={() => setIsDeleteReservations2(false)}>
                  <div>
                    <p className="font18 font-weight-600">{t("userList.deleteReservationCompleted")}</p>
                  </div>
                </ConfirmModal>

                {/* 방문자 초대 삭제 */}
                <ConfirmModal isOpen={isDeleteInvitations2} btnRightTitle={t("userList.delete")} onClick={() => setIsDeleteInvitations2(false)}>
                  <div>
                    <p className="font18 font-weight-600">{t("userList.deleteVisitorCompleted")}</p>
                  </div>
                </ConfirmModal>
                <ConfirmModal isOpen={alertModal.isOpen} btnRightTitle={t("common:confirm")} onClick={() => clickAlertModalConfirm()}>
                  <div>
                    <p className="font18 font-weight-600">{alertModal?.message}</p>
                  </div>
                </ConfirmModal>
                {/* 재발송 모달 */}
                {isReinviteModal.isOpen && (
                  <ConfirmModal
                    isOpen={true}
                    btnLeftTitle={t("common:cancel")}
                    btnRightTitle={t("common:confirm")}
                    onClose={() => setIsReinviteModal({ isOpen: false })}
                    onClick={() => handleConfirmReinviteModal(contractManage.contractMemberId as number)}
                  >
                    <div>
                      <p className="font18 font-weight-600">{t("userList.reinviteQuestion")}</p>
                      <p className="font15 font-weight-400 mt20">{t("userList.reinviteExpireKept")}</p>
                      <p className="font15 font-weight-400 mt5">
                        {t("userList.reinviteExpireDateLabel", {
                          date: contractManage.inviteExpireDate ? moment(contractManage.inviteExpireDate).format(ViewYmdFormat.FULL) : "",
                        })}
                      </p>
                    </div>
                  </ConfirmModal>
                )}
              </div>
            </>
          )}
        </div>
      )}
      {(contractManage.memberStatus === "MEMBER_CLOSE" || contractManage.memberStatus === "MEMBER_DELETE") && (
        <div className="contents-card__info-area">
          <p>
            {t("userList.suspendedDescLine1")}
            <br />
            {t("userList.suspendedDescLine2")}
          </p>
          <button type="button" className="icon-right-btn">
            {t("userList.contactUs")}
          </button>
        </div>
      )}
    </div>
  );
};

export default UserItem;
